import React, {useCallback, useEffect, useRef, useState} from 'react';
import {connect} from "react-redux";
import {compose} from "redux";
import DataGrid, {SelectColumn} from 'react-data-grid';
import {debounce} from "lodash";
import {Box, Button, Chip, Tooltip} from "@mui/material";
import 'react-data-grid/lib/styles.css';
import moment from "moment/moment";
import {IsAtBottom} from "../../reporting-table/ReactDataGridUtils";
import CustomSnackbar from "../../alert/CustomSnackbar";
import Spinner from "../../spinner/Spinner";
import {workspaceActions} from "../../../store/actions/workspaceAction";
import {alertActions} from "../../../store/actions/alertActions";
import {Delete, Sync} from "@mui/icons-material";
import {websiteDeploymentsActions} from "../../../store/actions/websiteDeploymentsAction";

import {WebsiteSectionHeader, WebsiteTableCard, WebsiteEmptyState, WebsiteDeleteDialog} from "../WebsitesDesign";
import {RocketLaunchOutlined} from "@mui/icons-material";

function DeploymentsTable(props) {


    const {
        alert, getReport, updateSortBy, updateOrderBy, updateSortColumns, loadMoreRows,
        getWorkspaceIDs, resetUpdatedWorkspaceValue, alertError, deleteDeployment, checkStatus
    } = props;

    const {data, isLoading, orderBy, deployment, sortBy, sortColumns, rerender} = props?.websiteDeploymentsState;
    const {plan} = props?.auth?.subscription;

    const [prompt, setPrompt] = useState(false);
    const [deleteDeploymentId, setDeleteDeploymentId] = useState(0);
    const [selectedRows, setSelectedRows] = useState(() => new Set());

    const getColumns = () => {
        let cachedColumns = JSON.parse(localStorage.getItem("website-deployments-columns"));
        let columns = [
            SelectColumn,
            {key: 'id', name: 'ID', hidden: false, width: 70, frozen: false, sortable: true, resizable: true},
            {
                key: 'workspace',
                name: 'Workspace',
                hidden: false,
                width: 100,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'integration',
                name: 'Integration',
                hidden: false,
                width: 100,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'websiteId',
                name: 'Website ID',
                hidden: false,
                width: 100,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'websiteName',
                name: 'Website Name',
                hidden: false,
                width: 120,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'distributionId',
                name: 'Deployment ID',
                hidden: false,
                width: 150,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'distributionDomain',
                name: 'Deployment Domain',
                hidden: false,
                width: 235,
                sortable: true,
                draggable: true,
                resizable: true,
                renderCell({row}) {
                    return (
                        <Box sx={{color: "primary.main"}}>{row?.distributionDomain}</Box>
                    );
                }
            },
            {
                key: 'alternateDomain',
                name: 'Alternate Domain',
                hidden: false,
                width: 190,
                sortable: true,
                draggable: true,
                resizable: true
            },
            {
                key: 'distributionStatus',
                name: 'Status',
                hidden: false,
                width: 100,
                sortable: true,
                draggable: true,
                resizable: true,
                renderCell({row}) {
                    if (row?.distributionStatus === "Deployed") {
                        return <Chip label={row?.distributionStatus} variant="outlined" color="primary"
                                     sx={{height: "24px", marginTop: "0px"}}/>
                    } else {
                        return <Chip label={row?.distributionStatus} variant="outlined" color="warning"
                                     sx={{height: "24px", marginTop: "-3px"}}/>
                    }
                }
            },
            {
                key: 'createdAt',
                name: 'Created At',
                hidden: false,
                width: 190,
                sortable: true,
                draggable: true,
                resizable: true,
                renderCell({row}) {
                    return row?.createdAt === null ? "-" : moment.utc(row?.createdAt).format("YYYY-MM-DD HH:mm") + " (UTC)"
                }
            },
        ];

        if (cachedColumns !== null && cachedColumns?.length !== columns.length) {
            return columns;
        }

        if (cachedColumns !== null && cachedColumns !== "" && cachedColumns !== undefined) {
            let sortedColumns = [];
            cachedColumns?.forEach(cachedCol => {
                columns?.forEach(defCol => {
                    if (cachedCol?.key === defCol?.key) {
                        defCol.width = cachedCol.width;
                        sortedColumns.push(defCol);
                    }
                });
            });
            return sortedColumns;
        }
        localStorage.setItem("website-deployments-columns", JSON.stringify(columns));
        return columns;
    };

    const gridRef = useRef(null);
    const pendingPage = useRef(null);
    const checkedData = useRef(null);

    const [columns, setColumns] = useState(getColumns());

    const onColumnsReorder = (sourceKey, targetKey) => {
        const sourceColumnIndex = columns.findIndex((c) => c.key === sourceKey);
        const targetColumnIndex = columns.findIndex((c) => c.key === targetKey);
        const reorderedColumns = [...columns];
        reorderedColumns.splice(targetColumnIndex, 0, reorderedColumns.splice(sourceColumnIndex, 1)[0]);
        localStorage.setItem("website-deployments-columns", JSON.stringify(reorderedColumns));
        setColumns(reorderedColumns);
    };

    const handleInfinityScroll = useCallback((event) => {
        if (isLoading || pendingPage.current !== null || !IsAtBottom(event)) {
            return;
        }
        const page = Number(data?.page);
        const pageSize = Number(data?.recordsPerPage);
        const total = Number(data?.totalRecords);
        if (page >= 1 && pageSize > 0 && data?.records?.length && data?.recordsReturned !== 0 && page < Math.ceil(total / pageSize)) {
            pendingPage.current = page + 1;
            loadMoreRows(deployment, page + 1, orderBy, sortBy, getWorkspaceIDs()).finally(() => {
                pendingPage.current = null;
            });
        }
    }, [isLoading, data, deployment, orderBy, sortBy, loadMoreRows, getWorkspaceIDs]);

    // Small pages may not overflow the grid yet, so there is no scroll event to load the next one.
    useEffect(() => {
        if (isLoading || checkedData.current === data) return;
        const frame = requestAnimationFrame(() => {
            const element = gridRef.current?.element;
            if (!element || element.clientHeight === 0) return;
            checkedData.current = data;
            handleInfinityScroll({currentTarget: element});
        });
        return () => cancelAnimationFrame(frame);
    }, [data, isLoading, handleInfinityScroll]);

    const handleGetReport = () => {
        gridRef?.current?.element?.scrollTo(0, 0);
        getReport(deployment, 1, orderBy, sortBy, getWorkspaceIDs());
    };

    const cacheColumnResize = (idx, width) => {
        let cachedColumns = JSON.parse(localStorage.getItem("website-deployments-columns"));
        let modColumns = [];
        cachedColumns?.forEach((item, index) => {
            if (index === idx) {
                item.width = width;
            }
            modColumns.push(item);
        });
        localStorage.setItem("website-deployments-columns", JSON.stringify(modColumns));
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleColumnResize = useCallback(debounce(cacheColumnResize, 50), []);

    const handleClickOnCheckStatus = () => {
        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        if (selectedRows.size === 1) {
            for (const selectedRowId of selectedRows) {
                checkStatus(selectedRowId);
            }
        }
    };

    const handleDeletePrompt = () => {

        if (plan === "Free") {
            alertError("This feature is not available in Free subscription plan.");
            return false;
        }

        if (selectedRows.size === 1) {
            for (const selectedRowId of selectedRows) {
                setDeleteDeploymentId(selectedRowId);
                setPrompt(true);
            }
        }
    };

    const handleOnClickDeleteDeployment = (prompt) => {
        if (!prompt) {
            setPrompt(false);
            return false;
        } else {
            deleteDeployment(deleteDeploymentId);
            setPrompt(false);
        }
    };

    useEffect(() => {
        handleGetReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getReport, deployment, sortBy, orderBy]);

    // Only for rerender
    useEffect(() => {
        if (rerender) {
            handleGetReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerender]);

    useEffect(() => {
        if (sortColumns.length === 0) {
            updateSortBy("");
            updateOrderBy("");
            return () => {
            }
        }

        const {columnKey, direction} = sortColumns[0];
        updateOrderBy(columnKey);
        updateSortBy(direction);

    }, [updateOrderBy, updateSortBy, sortColumns]);

    // 6. Rerender if user workspaces has changed.
    useEffect(() => {
        if (props?.workspace?.updated !== null) {
            handleGetReport();
            resetUpdatedWorkspaceValue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.workspace?.updated])

    return <Box>
        <WebsiteSectionHeader title="Deployments"
                              description="Monitor your website deployments. Point your domain’s CNAME record to the deployment domain to enable HTTPS.">

        </WebsiteSectionHeader>
        {prompt && <WebsiteDeleteDialog question="Delete deployment?"
                                        text="Are you sure you want to permanently delete this deployment?"
                                        confirmLabel="Delete deployment" confirm={handleOnClickDeleteDeployment}/>}
        {alert.errorMsg && <CustomSnackbar message={alert.errorMsg} timeout={8000}/>}
        {alert.successMsg && <CustomSnackbar message={alert.successMsg} severity="success" timeout={8000}/>}
        <WebsiteTableCard title="Deployments"
                          count={isLoading ? null : data?.totalRecords ?? data?.records?.length ?? 0}
                          rowCount={data?.records?.length || 0} selectedCount={selectedRows.size} actions={<>
            <Button variant="outlined" color="inherit" startIcon={<Sync/>} disabled={selectedRows.size < 1}
                    onClick={handleClickOnCheckStatus}>Check status</Button>
            <Tooltip title="Delete deployment"><span><Button variant="outlined" color="error"
                                                             aria-label="Delete deployment"
                                                             disabled={selectedRows.size !== 1}
                                                             onClick={handleDeletePrompt}
                                                             sx={{minWidth: 35, px: 1}}><Delete
                fontSize="small"/></Button></span></Tooltip>
        </>}>
            {isLoading && <Spinner size={32} overComponentBox/>}
            {columns !== null && (
                <DataGrid
                    ref={gridRef}
                    rowKeyGetter={(row) => row?.id}
                    columns={columns}
                    onColumnsReorder={onColumnsReorder}
                    rows={data?.records ? data.records : []}
                    defaultColumnOptions={{
                        sortable: true,
                        resizable: true,
                        draggable: true
                    }}
                    onColumnResize={(idx, width) => handleColumnResize(idx, width)}
                    sortColumns={sortColumns}
                    onSortColumnsChange={updateSortColumns}
                    selectedRows={selectedRows}
                    onSelectedRowsChange={setSelectedRows}
                    onSelectedCellChange={({rowIdx, row, column}) => {
                        if (rowIdx !== -1 && column?.key !== "select-row") {
                            setSelectedRows(new Set().add(row?.id));
                        }
                    }}
                    onScroll={handleInfinityScroll}
                    enableVirtualization={false}
                    className="website-data-grid"
                    rowHeight={55}
                    headerRowHeight={48}
                    aria-label="Deployments"
                />
            )}
            {!isLoading && !data?.records?.length &&
                <WebsiteEmptyState icon={RocketLaunchOutlined} title="No deployments yet"
                                   description="Deploy a website from the S3 Buckets tab to publish it on your domain."/>}
        </WebsiteTableCard>
    </Box>;
}

const mapStateToProps = (state) => {
    return {
        websiteDeploymentsState: state.websiteDeployments,
        auth: state.auth,
        alert: state.alert,
        workspace: state.workspace
    }
};

const actionCreators = {
    getReport: websiteDeploymentsActions.read,
    loadMoreRows: websiteDeploymentsActions.readMoreRows,
    updateOrderBy: websiteDeploymentsActions.updateOrderBy,
    updateSortBy: websiteDeploymentsActions.updateSortBy,
    updateSortColumns: websiteDeploymentsActions.updateSortColumns,
    deleteDeployment: websiteDeploymentsActions.deleteDeployment,
    checkStatus: websiteDeploymentsActions.checkStatus,
    getWorkspaceIDs: workspaceActions.getWorkspaceIDs,
    resetUpdatedWorkspaceValue: workspaceActions.resetUpdatedWorkspaceValue,
    alertError: alertActions.error,
};

export default compose(connect(mapStateToProps, actionCreators))(DeploymentsTable);
